import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import {Paper, Typography, Tooltip, IconButton, Fab} from '@material-ui/core';
import {Add, Refresh} from '@material-ui/icons';
import MaterialTable from 'material-table';
import {hasPermission, localization} from '../../sc-utilities';
import Toolbar from '../../sc-utilities/Toolbar'
import * as action from "../../np-core/Auth/store/authActions";
import * as workDiagnoseReportActions from "./store/workDiagnoseReportActions";

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16,
        marginTop: '-2px'
    },
    customToolbar: {
        marginLeft: 14,
        width: "900px"
    },
    folderStructureIcon: {
        marginRight: theme.spacing(),
    },
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];

const WorkDiagnoseReportList = props => {
    const { classes, translations, tableColumns, tableRef, activeCustomerID, saveColumnsFilter, onFetchWorkDiagnoseReportCount, onFetchWorkDiagnoseReportList, permissions, productID, onGenerate } = props;

    const columnObject = useMemo(() => tableColumns
        // eslint-disable-next-line react-hooks/exhaustive-deps
        , []);
    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        filterCellStyle: { maxWidth: '200px' },
        selectionProps: rowData => {
            let disabled = false;
            if (rowData.Deleted) {
                disabled = true;
            }
            rowData.tableData.disabled = disabled;
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - 378 < 300 ? 300 : window.innerHeight - 378,
        maxBodyHeight: window.innerHeight - 378,
        pageSizeOptions: rowsPageSizeInit,
        exportButton: false,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? '#f3f3f3' : '#ffffff' };
        },
        columnsButton: true,
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    };

    async function fetchTableData(query) {
        const q = filterQuery(query.filters?? []);
        query['ActiveCustomerID'] = activeCustomerID;
        q["ErasureReportType"] = ["diagnose-pdf"];
        q['ProductID'] = productID;
        query['q'] = q;
        query['ProductID'] = productID
        delete query["filters"];
        delete query["orderDirection"];
        delete query["search"];
        delete query["totalCount"];
        const count = await onFetchWorkDiagnoseReportCount(query);
        const result = count ? await onFetchWorkDiagnoseReportList(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit });
        return { data: result || [], page: query.page, pageSize: pageSize, totalCount: count || 0 };
    }

    const handleGenerateReport = async () => {
        await onGenerate({ ActiveCustomerID: activeCustomerID, ProductID: productID, ErasureReportType: 'diagnose-pdf' }, translations, 'diagnose-pdf');
        tableRef.current && tableRef.current.onQueryChange()
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                <Typography className={classes.typography}>{translations["work_diagnose_report"] || "Diagnose report"}</Typography>
                {hasPermission(permissions, "generate_new_report") ? (
                    <Fab
                        variant="extended"
                        size="small"
                        color="primary"
                        aria-label="Add"
                        style={{ borderRadius: '1px' }}
                        className={classes.fab}
                        onClick={handleGenerateReport}
                    >
                        <Add size="small" />
                        {translations["generate_new_report"] || "Generate new report"}
                    </Fab>
                ) : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
        </div>
    };

    const columnHidden = newColumnState => {
        let objToSendColumn = {};
        let objColumns = {};
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden;
            return objColumns;
        })
        objToSendColumn[`WorkDiagnoseReport`] = objColumns;
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID });
        return newColumnState;
    }

    return (
        <Paper>
             <MaterialTable
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || [],
    };
};

const mapDispatchToProps = dispatch => {
    return {
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onFetchWorkDiagnoseReportCount: data => dispatch(workDiagnoseReportActions.fetchWorkDiagnoseReportCount(data)),
        onFetchWorkDiagnoseReportList: data => dispatch(workDiagnoseReportActions.fetchWorkDiagnoseReportList(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(WorkDiagnoseReportList));
